<template>
  <div @mouseenter="onComponentMouseEnter" @mouseleave="onComponentMouseLeave" :class="[baseCss, hidden ? [`${baseCss}-hidden`] : null, disabled ? [`${baseCss}-disabled`, 'pointer-events-none', 'opacity-50'] : null, 'text-center', autoHide ? `${baseCss}-autohide` : null, magnification ? `${baseCss}-magnify` : null, 'fixed', `${baseCss}-${location}`, 'transition-all', `duration-${animationSpeed}`]" :style="style" role="navigation">
    <div :class="['rounded-md', 'transition-all', `duration-${animationSpeed}`]" :style="{marginLeft: `${['top', 'bottom'].includes(location) ? locationOffset : 0}px`, marginRight: `${['top', 'bottom'].includes(location) ? locationOffset : 0}px`}">
      <table-layout y-align="middle" x-align="center" :class="[`${baseCss}-items`]">
        <row>
          <cell @mouseenter.native="onItemMouseEnter(index)" :data-index="index" @mouseleave.native="onItemMouseLeave(index)" @click="onItemClick(item)" x-align="center" v-for="(item, index) in items" :key="index" :class="[`${baseCss}-items-item`, `${baseCss}-items-cell`, item.disabled ? [`${baseCss}-items-item-disabled`, 'pointer-events-none', 'opacity-50'] : null, 'p-1', 'transition-all', `duration-${animationSpeed}`]" v-tooltip="{text: item.hint, position: hintPosition}">
            <div :class="['cursor-default', item.jump ? 'bcq-macos-dock-icon-jump' : null]">
              <table-layout y-align="middle" x-align="center">
                <row>
                  <macos-dock-app-marker v-if="showOpenAppIndicators && location === 'left'" :open="item.open" :class="['pr-1']" />
                  <cell>
                    <badge :text="item.notifications || 0" :class="['transition-all', `duration-${animationSpeed}`]">
                      <img :src="item.iconUrl" :alt="item.iconUrl" :style="iconStyle" :class="['inline-block', 'rounded-md', showOpenAppIndicators ? 'mb-1' : null]" />
                    </badge>
                  </cell>
                  <macos-dock-app-marker v-if="showOpenAppIndicators && location === 'right'" :open="item.open" :class="['pl-1']" />
                </row>
                <row v-if="showOpenAppIndicators && ['top', 'bottom'].includes(location)">
                  <macos-dock-app-marker :open="item.open" />
                </row>
              </table-layout>
            </div>
          </cell>
        </row>
      </table-layout>
    </div>
    <reactive-style>
    .bcq-macos-dock.bcq-macos-dock-magnify .bcq-macos-dock-items-item-hovered .bcq-badge
    {
      transform: scale({{ magnificationRatio[0] }});
    }

    .bcq-macos-dock.bcq-macos-dock-magnify .bcq-macos-dock-items-item-prev .bcq-badge,
    .bcq-macos-dock.bcq-macos-dock-magnify .bcq-macos-dock-items-item-hovered + .bcq-macos-dock-items-item .bcq-badge
    {
      transform: scale({{ magnificationRatio[1] }});
    }

    .bcq-macos-dock.bcq-macos-dock-magnify .bcq-macos-dock-items-item-prev-prev .bcq-badge,
    .bcq-macos-dock.bcq-macos-dock-magnify .bcq-macos-dock-items-item-hovered + .bcq-macos-dock-items-item + .bcq-macos-dock-items-item .bcq-badge
    {
      transform: scale(magnificationRatio[2]);
    }
    </reactive-style>
  </div>
</template>
<script>
import methods from '../../mixins/methods'

export default {
  name: 'MacosDock',
  mixins: [
    methods
  ],
  data () {
    return {
      hidden: false,
      baseCss: this.componentBaseCss('macos-dock', []),
      magnificationRatio: [2, 1.5, 1.25]
    }
  },
  watch: {
    autoHide () {
      this.setHidden()
    }
  },
  computed: {
    style () {
      const offset = this.locationOffset
      return {
        zIndex: this.zIndex,
        [this.location]: this.hidden ? this.$el[['top', 'bottom'].includes(this.location) ? 'clientHeight' : 'clientWidth'] * -1 + offset * 2 + 'px' : `${offset}px`
      }
    },
    iconStyle () {
      return {
        aspectRatio: '1/1',
        width: `${this.itemSize}px`
      }
    },
    hintPosition () {
      switch (this.location) {
        case 'top':
          return 'bottom'
        case 'right':
          return 'left'
        case 'bottom':
          return 'top'
        case 'left':
          return 'right'
      }
    }
  },
  props: {
    location: {
      type: String,
      default: 'bottom',
      validator: value => {
        return ['top', 'right', 'bottom', 'left'].includes(value)
      }
    },
    magnification: {
      type: Boolean,
      default: true
    },
    itemSize: {
      type: Number,
      default: 32
    },
    items: {
      type: Array,
      default () {
        return []
      }
    },
    autoHide: {
      type: Boolean,
      default: false
    },
    animationSpeed: {
      type: Number,
      default: 300,
      validator: value => {
        return [300, 500, 75, 100, 150, 200, 700, 1000].includes(value)
      }
    },
    zIndex: {
      type: Number,
      default: 1000
    },
    locationOffset: {
      type: Number,
      default: 4
    },
    showOpenAppIndicators: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  mounted () {
    this.setHidden()
  },
  methods: {
    setHidden () {
      this.hidden = this.autoHide
    },
    onItemClick (item) {
      this.$emit('item-click', item)
    },
    itemsCssClass () {
      return `${this.baseCss}-items-item`
    },
    onItemMouseEnter (index) {
      const itemsCss = this.itemsCssClass()
      const currentItem = this.getItemByIndex(index)
      const prevItem = this.getItemByIndex(index - 1)
      const prevPrevItem = this.getItemByIndex(index - 2)
      const itemSize = this.itemSize
      const magnificationRatio = this.magnificationRatio
      if (!this.magnification) {
        return
      }
      currentItem.classList.add(`${itemsCss}-hovered`)
      currentItem.style.width = `${itemSize * magnificationRatio[0]}px`
      if (prevItem) {
        prevItem.classList.add(`${itemsCss}-prev`)
        prevItem.style.width = `${itemSize * magnificationRatio[1]}px`
      }
      if (prevPrevItem) {
        prevPrevItem.classList.add(`${itemsCss}-prev-prev`)
        prevPrevItem.style.width = `${itemSize * magnificationRatio[2]}px`
      }
      currentItem.querySelector('div').classList.remove('bcq-macos-dock-icon-jump')
    },
    onItemMouseLeave (index) {
      const itemsCss = this.itemsCssClass()
      const currentItem = this.getItemByIndex(index)
      const prevItem = this.getItemByIndex(index - 1)
      const prevPrevItem = this.getItemByIndex(index - 2)
      if (!this.magnification) {
        return
      }
      currentItem.classList.remove(`${itemsCss}-hovered`)
      currentItem.style.width = ''
      if (prevItem) {
        prevItem.classList.remove(`${itemsCss}-prev`)
        prevItem.style.width = ''
      }
      if (prevPrevItem) {
        prevPrevItem.classList.remove(`${itemsCss}-prev-prev`)
        prevPrevItem.style.width = ''
      }
    },
    getItemByIndex (index) {
      return this.$el.querySelector(`[data-index="${index}"]`)
    },
    onComponentMouseEnter () {
      if (this.autoHide) {
        this.hidden = false
      }
    },
    onComponentMouseLeave () {
      if (this.autoHide) {
        this.hidden = true
      }
    }
  }
}
</script>
<style>
.bcq-macos-dock
{
  width: 100%;
}

.bcq-macos-dock > div
{
  backdrop-filter: blur(8px);
  border: solid 1px rgba(255, 255, 255, .25);
  width: auto;
  display: inline-block;
}

.bcq-macos-dock .bcq-macos-dock-items
{
  width: auto;
  display: inline-table;
  vertical-align: middle;
}

.bcq-macos-dock .bcq-macos-dock-items .bcq-macos-dock-items-item:active img
{
  filter: brightness(50%);
}

.bcq-macos-dock.bcq-macos-dock-top,
.bcq-macos-dock.bcq-macos-dock-bottom
{
  left: 50%;
  transform: translateX(-50%);
}

.bcq-macos-dock.bcq-macos-dock-left,
.bcq-macos-dock.bcq-macos-dock-right
{
  top: 50%;
  transform: translateY(-50%);
  width: auto;
}

.bcq-macos-dock.bcq-macos-dock-left .bcq-macos-dock-item,
.bcq-macos-dock.bcq-macos-dock-left .bcq-macos-dock-items-row,
.bcq-macos-dock.bcq-macos-dock-left .bcq-macos-dock-items-cell,
.bcq-macos-dock.bcq-macos-dock-right .bcq-macos-dock-item,
.bcq-macos-dock.bcq-macos-dock-right .bcq-macos-dock-items-row,
.bcq-macos-dock.bcq-macos-dock-right .bcq-macos-dock-items-cell
{
  display: block;
}

.bcq-macos-dock .bcq-macos-dock-open-app-indicator
{
  width: 4px;
  aspect-ratio: 1/1;
  margin: auto;
}

.bcq-macos-dock.bcq-macos-dock-magnify .bcq-macos-dock-items-item .bcq-badge
{
  position: relative;
}

.bcq-macos-dock.bcq-macos-dock-magnify .bcq-macos-dock-items-item-hovered .bcq-badge
{
  z-index: 100;
}

.bcq-macos-dock.bcq-macos-dock-magnify .bcq-macos-dock-items-item-prev .bcq-badge,
.bcq-macos-dock.bcq-macos-dock-magnify .bcq-macos-dock-items-item-hovered + .bcq-macos-dock-items-item .bcq-badge
{
  z-index: 80;
}

.bcq-macos-dock.bcq-macos-dock-magnify .bcq-macos-dock-items-item-prev-prev .bcq-badge,
.bcq-macos-dock.bcq-macos-dock-magnify .bcq-macos-dock-items-item-hovered + .bcq-macos-dock-items-item + .bcq-macos-dock-items-item .bcq-badge
{
  z-index: 60;
}

.bcq-macos-dock.bcq-macos-dock-hidden.bcq-macos-dock-top > div,
.bcq-macos-dock.bcq-macos-dock-hidden.bcq-macos-dock-right > div,
.bcq-macos-dock.bcq-macos-dock-hidden.bcq-macos-dock-bottom > div,
.bcq-macos-dock.bcq-macos-dock-hidden.bcq-macos-dock-left > div
{
  visibility: hidden;
}

.bcq-macos-dock.bcq-macos-dock-hidden.bcq-macos-dock-top > div
{
  transform: translateY(-100%);
}

.bcq-macos-dock.bcq-macos-dock-hidden.bcq-macos-dock-right > div
{
  transform: translateX(100%);
}

.bcq-macos-dock.bcq-macos-dock-hidden.bcq-macos-dock-bottom > div
{
  transform: translateY(100%);
}

.bcq-macos-dock.bcq-macos-dock-hidden.bcq-macos-dock-left > div
{
  transform: translateX(-100%);
}

.bcq-macos-dock.bcq-macos-dock-top:not(.bcq-macos-dock-hidden) > div,
.bcq-macos-dock.bcq-macos-dock-right:not(.bcq-macos-dock-hidden) > div,
.bcq-macos-dock.bcq-macos-dock-bottom:not(.bcq-macos-dock-hidden) > div,
.bcq-macos-dock.bcq-macos-dock-left:not(.bcq-macos-dock-hidden) > div
{
  transform: translateY(0);
  visibility: visible;
}

.bcq-macos-dock .bcq-badge > span
{
  z-index: 200;
  margin-top: -4px;
  margin-right: -4px;
}

.bcq-macos-dock .bcq-macos-dock-icon-jump
{
  transform-origin: 50% 50%;
  animation: bcq-macos-dock-icon-jump 1.5s ease-out normal infinite;
}

@keyframes bcq-macos-dock-icon-jump
{
  0% {
    transform: translate3d(0, 0, 0);
  }
  10% {
    transform: translate3d(0, -100%, 0);
  }
  20% {
    transform: translate3d(0, 0, 0);
  }
  30% {
    transform: translate3d(0, -50%, 0)
  }
  40% {
    transform: translate3d(0, 0, 0)
  }
  50% {
    transform: translate3d(0, -25%, 0)
  }
  60% {
    transform: translate3d(0, 0, 0)
  }
  70% {
    transform: translate3d(0, -5%, 0)
  }
  80% {
    transform: translate3d(0, 0, 0)
  }
  90% {
    transform: translate3d(0, -2.5%, 0)
  }
  100% {
    transform: translate3d(0, 0, 0)
  }
}
</style>



// WEBPACK FOOTER //
// src/bcq-ui/components/macos-dock/macos-dock.vue